import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {IMAGE_MIME_TYPE} from '@mantine/dropzone';
import React from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import type {IFormFieldV2} from 'src/types/FormV2';
import type {InstanceFormValues, InstanceSectionProps} from './SectionProps';
import {ViewSwitch} from 'src/design/atoms/ViewField';

const logoField: IFormFieldV2 = {
  name: 'logoUrl',
  label: 'Instance Logo',
  type: 'file',
  fileUploadOptions: {
    allowMultiple: false,
    allowedFileTypes: IMAGE_MIME_TYPE,
    isHideUploadSuccessEnabled: true,
    renderUploadsAsImage: true,
    maxFileSize: 512000,
    simplifiedUploader: true,
  },
};

const SubSectionLabel: React.FC<{children: React.ReactNode}> = ({children}) => (
  <Text fw={600} size="sm" c="dimmed">
    {children}
  </Text>
);

export const OtherSettingsSection: React.FC<InstanceSectionProps> = ({
  mode,
}) => {
  const {control} = useFormContext<InstanceFormValues>();
  const formData = useWatch({control});
  const isViewMode = mode === 'view';

  if (isViewMode) {
    return (
      <SectionCard title="Other Settings" data-testid="section-other-settings">
        <Box p="md">
          <Stack gap="md">
            <Box>
              <SubSectionLabel>Email</SubSectionLabel>
              <Box mt="xs">
                <ViewSwitch
                  label="Plaintext Email Only"
                  value={!!formData.emailPlaintextOnly}
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <SubSectionLabel>Avatars</SubSectionLabel>
              <Group mt="xs" gap="xl">
                <ViewSwitch
                  label="Employee Avatar Upload"
                  value={!!formData.isAvatarRequiredEmployees}
                />
                <ViewSwitch
                  label="Contractor Avatar Upload"
                  value={!!formData.isAvatarRequiredContractors}
                />
              </Group>
            </Box>

            {formData.logoUrl && (
              <>
                <Divider />
                <Box>
                  <SubSectionLabel>Branding</SubSectionLabel>
                  <Box mt="xs">
                    <Text size="sm" c="dimmed">
                      Logo configured
                    </Text>
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Other Settings" data-testid="section-other-settings">
      <Box p="md">
        <Stack gap="md">
          <Box>
            <SubSectionLabel>Email</SubSectionLabel>
            <Box mt="xs">
              <Controller
                name="emailPlaintextOnly"
                control={control}
                render={({field}) => (
                  <Switch
                    data-testid="form-field-emailPlaintextOnly"
                    label="Plaintext Email Only"
                    description="Send all notification emails as plaintext instead of HTML."
                    color="green.8"
                    checked={!!field.value}
                    onChange={e => field.onChange(e.currentTarget.checked)}
                  />
                )}
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <SubSectionLabel>Avatars</SubSectionLabel>
            <Grid mt="xs" gutter="md">
              <Grid.Col span={{base: 12, md: 6}}>
                <Controller
                  name="isAvatarRequiredEmployees"
                  control={control}
                  render={({field}) => (
                    <Switch
                      data-testid="form-field-isAvatarRequiredEmployees"
                      label="Employee Avatar Upload"
                      description="Prompt employees to upload a profile avatar on login."
                      color="green.8"
                      checked={!!field.value}
                      onChange={e => field.onChange(e.currentTarget.checked)}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <Controller
                  name="isAvatarRequiredContractors"
                  control={control}
                  render={({field}) => (
                    <Switch
                      data-testid="form-field-isAvatarRequiredContractors"
                      label="Contractor Avatar Upload"
                      description="Prompt contractors to upload a profile avatar on login."
                      color="green.8"
                      checked={!!field.value}
                      onChange={e => field.onChange(e.currentTarget.checked)}
                    />
                  )}
                />
              </Grid.Col>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <SubSectionLabel>Branding</SubSectionLabel>
            <Box mt="xs">
              <Controller
                name="logoUrl"
                control={control}
                render={({field}) => (
                  <FormFileUploadV2
                    field={logoField}
                    value={field.value || null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                )}
              />
            </Box>
          </Box>
        </Stack>
      </Box>
    </SectionCard>
  );
};
